import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Calendar, Heart, Trash2, AlertCircle, CalendarDays, TrendingUp, BarChart3 } from 'lucide-react';
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PeriodTracker = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState({
    cycle_length: 28,
    period_duration: 5,
    last_period_start: null
  });
  
  // Form state
  const getLocalDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [startDate, setStartDate] = useState(getLocalDate());
  const [endDate, setEndDate] = useState('');
  const [cycleLength, setCycleLength] = useState(28);
  const [periodDuration, setPeriodDuration] = useState(5);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (user?.id) {
      fetchPeriodHistory();
      fetchPeriodSettings();
    }
  }, [user?.id]);

  const fetchPeriodHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users/${user.id}/period-tracker/history`);
      
      if (response.data.success) {
        setHistory(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching period history:', error);
      toast.error('Error loading period history');
    } finally {
      setLoading(false);
    }
  };

  const fetchPeriodSettings = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.id}/period-tracker/settings`);
      
      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
        setCycleLength(response.data.data.cycle_length);
        setPeriodDuration(response.data.data.period_duration);
      }
    } catch (error) {
      console.error('Error fetching period settings:', error);
    }
  };

  const calculateNextPeriod = () => {
    if (!settings.last_period_start) return null;
    
    const lastPeriod = new Date(settings.last_period_start);
    const nextPeriod = new Date(lastPeriod);
    nextPeriod.setDate(lastPeriod.getDate() + settings.cycle_length);
    
    return nextPeriod;
  };

  const calculateOvulation = () => {
    const nextPeriod = calculateNextPeriod();
    if (!nextPeriod) return null;
    
    const ovulation = new Date(nextPeriod);
    ovulation.setDate(nextPeriod.getDate() - 14); // Ovulation is typically 14 days before next period
    
    return ovulation;
  };

  const calculateDaysUntilOrOver = () => {
    const nextPeriod = calculateNextPeriod();
    if (!nextPeriod) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    nextPeriod.setHours(0, 0, 0, 0);
    
    const diffTime = nextPeriod - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const calculateAverageCycle = () => {
    if (history.length < 2) return null;
    
    const cycles = [];
    for (let i = 0; i < history.length - 1; i++) {
      const current = new Date(history[i].start_date);
      const previous = new Date(history[i + 1].start_date);
      const daysDiff = Math.round((current - previous) / (1000 * 60 * 60 * 24));
      if (daysDiff > 0 && daysDiff < 60) { // Filter out unrealistic values
        cycles.push(daysDiff);
      }
    }
    
    if (cycles.length === 0) return null;
    
    const average = cycles.reduce((a, b) => a + b, 0) / cycles.length;
    return Math.round(average);
  };

  const prepareCycleChartData = () => {
    if (history.length < 2) return [];
    
    const chartData = [];
    
    // Sort history by date (oldest first for chronological chart)
    const sortedHistory = [...history].sort((a, b) => 
      new Date(a.start_date) - new Date(b.start_date)
    );
    
    // Calculate cycle length between consecutive periods
    for (let i = 1; i < sortedHistory.length; i++) {
      const current = new Date(sortedHistory[i].start_date);
      const previous = new Date(sortedHistory[i - 1].start_date);
      const cycleLength = Math.round((current - previous) / (1000 * 60 * 60 * 24));
      
      if (cycleLength > 0 && cycleLength < 60) {
        chartData.push({
          date: formatDate(sortedHistory[i].start_date),
          cycleLength: cycleLength,
          isNormal: cycleLength >= 26 && cycleLength <= 32 // Normal range: 26-32 days
        });
      }
    }
    
    return chartData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!startDate) {
      toast.error('Please enter period start date');
      return;
    }

    try {
      setSaving(true);
      
      const data = {
        start_date: startDate,
        end_date: endDate || null,
        cycle_length: parseInt(cycleLength),
        period_duration: parseInt(periodDuration),
        notes: notes
      };

      await axios.post(`${API}/users/${user.id}/period-tracker`, data);
      
      toast.success('Period entry saved successfully!');
      
      // Reset form
      setStartDate(getLocalDate());
      setEndDate('');
      setNotes('');
      
      // Refresh data
      fetchPeriodHistory();
      fetchPeriodSettings();
      
    } catch (error) {
      console.error('Error saving period entry:', error);
      toast.error('Error saving period entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (startDate) => {
    if (!window.confirm('Are you sure you want to delete this period entry?')) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${user.id}/period-tracker/${startDate}`);
      toast.success('Period entry deleted');
      fetchPeriodHistory();
      fetchPeriodSettings();
    } catch (error) {
      console.error('Error deleting period entry:', error);
      toast.error('Error deleting period entry');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const nextPeriod = calculateNextPeriod();
  const ovulation = calculateOvulation();
  const daysUntilOrOver = calculateDaysUntilOrOver();
  const averageCycle = calculateAverageCycle();

  return (
    <div className="space-y-6">
      {/* Header Banner - matching Daily Wellness Tracker */}
      <Card className="bg-white border-burgundy/20 overflow-hidden rounded-none">
        <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
          <CardTitle className="font-serif">Period Tracker</CardTitle>
          <CardDescription className="text-cream/90">Track your cycle and predict your next period</CardDescription>
        </CardHeader>
      </Card>

      {/* Predictions Card */}
      <Card className="bg-white border-burgundy/20">
        <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
          <CardTitle className="font-serif flex items-center">
            <CalendarDays className="h-5 w-5 mr-2" />
            Cycle Predictions
          </CardTitle>
        </CardHeader>
        <CardContent>
          {settings.last_period_start ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Next Period */}
              <div className="bg-white rounded-lg p-4 border border-burgundy/10">
                <div className="text-sm text-burgundy/60 mb-1">Next Period Expected</div>
                <div className="text-2xl font-bold text-burgundy">
                  {nextPeriod ? formatDate(nextPeriod.toISOString().split('T')[0]) : 'N/A'}
                </div>
                {daysUntilOrOver !== null && (
                  <div className="mt-2">
                    {daysUntilOrOver > 0 ? (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        In {daysUntilOrOver} days
                      </Badge>
                    ) : daysUntilOrOver === 0 ? (
                      <Badge className="bg-pink-500 text-white">
                        Expected Today
                      </Badge>
                    ) : (
                      <Badge className="bg-orange-100 text-orange-700">
                        {Math.abs(daysUntilOrOver)} days overdue
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Ovulation */}
              <div className="bg-white rounded-lg p-4 border border-burgundy/10">
                <div className="text-sm text-burgundy/60 mb-1">Ovulation Date</div>
                <div className="text-2xl font-bold text-burgundy">
                  {ovulation ? formatDate(ovulation.toISOString().split('T')[0]) : 'N/A'}
                </div>
                <div className="mt-2">
                  <Badge variant="outline" className="border-gold text-gold">
                    Fertility Window
                  </Badge>
                </div>
              </div>

              {/* Average Cycle */}
              <div className="bg-white rounded-lg p-4 border border-burgundy/10">
                <div className="text-sm text-burgundy/60 mb-1">Average Cycle Length</div>
                <div className="text-2xl font-bold text-burgundy">
                  {averageCycle ? `${averageCycle} days` : `${settings.cycle_length} days`}
                </div>
                <div className="mt-2">
                  <Badge variant="outline" className="border-burgundy text-burgundy">
                    {history.length > 1 ? `Based on ${history.length} cycles` : 'Default setting'}
                  </Badge>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-burgundy/30 mx-auto mb-3" />
              <p className="text-burgundy/70">No period data yet. Add your first entry below to start tracking!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add New Entry Card */}
      <Card className="bg-white border-burgundy/20">
        <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
          <CardTitle className="font-serif">Log Period</CardTitle>
          <CardDescription className="text-cream/90">Track your period start date and cycle information</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Period Start Date *
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="border-burgundy/30 focus:border-gold"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Period End Date (Optional)
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="border-burgundy/30 focus:border-gold"
                  min={startDate}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Typical Cycle Length (days)
                </label>
                <Input
                  type="number"
                  value={cycleLength}
                  onChange={(e) => setCycleLength(e.target.value)}
                  className="border-burgundy/30 focus:border-gold"
                  min="20"
                  max="45"
                />
                <p className="text-xs text-burgundy/60 mt-1">Average: 28 days</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-burgundy mb-2">
                  Period Duration (days)
                </label>
                <Input
                  type="number"
                  value={periodDuration}
                  onChange={(e) => setPeriodDuration(e.target.value)}
                  className="border-burgundy/30 focus:border-gold"
                  min="1"
                  max="10"
                />
                <p className="text-xs text-burgundy/60 mt-1">Average: 5 days</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-burgundy mb-2">
                Notes (Optional)
              </label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any symptoms, observations, or notes..."
                className="border-burgundy/30 focus:border-gold min-h-[80px]"
              />
            </div>

            <Button 
              type="submit" 
              disabled={saving || !startDate}
              className="w-full bg-burgundy hover:bg-burgundy/80 text-white"
            >
              {saving ? 'Saving...' : 'Save Period Entry'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* History Card */}
      <Card className="bg-white border-burgundy/20">
        <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
          <CardTitle className="font-serif flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Period History
          </CardTitle>
          <CardDescription className="text-cream/90">
            Track your cycle patterns over time
            {averageCycle && history.length > 1 && (
              <span className="ml-2 text-cream">
                • Average cycle: {averageCycle} days
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy"></div>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-burgundy/60">
              No period entries yet. Add your first entry above to start tracking!
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((entry, index) => {
                // Calculate cycle length between this and next entry
                let cycleDays = null;
                if (index < history.length - 1) {
                  const current = new Date(entry.start_date);
                  const next = new Date(history[index + 1].start_date);
                  cycleDays = Math.round((current - next) / (1000 * 60 * 60 * 24));
                }

                return (
                  <div 
                    key={entry.id} 
                    className="flex items-center justify-between p-4 border border-burgundy/10 rounded-lg hover:bg-burgundy/5 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Heart className="h-5 w-5 text-pink-500" />
                        <div>
                          <div className="font-semibold text-burgundy">
                            {formatDate(entry.start_date)}
                            {entry.end_date && ` - ${formatDate(entry.end_date)}`}
                          </div>
                          <div className="text-sm text-burgundy/70">
                            {cycleDays && (
                              <span className="mr-3">Cycle: {cycleDays} days</span>
                            )}
                            Duration: {entry.period_duration} days
                          </div>
                          {entry.notes && (
                            <p className="text-sm text-burgundy/60 mt-1 italic">"{entry.notes}"</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(entry.start_date)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cycle Length Chart - Bottom of Page */}
      {history.length >= 2 && (
        <Card className="bg-white border-burgundy/20">
          <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream">
            <CardTitle className="font-serif flex items-center">
              <BarChart3 className="h-5 w-5 mr-2" />
              Cycle Length History
            </CardTitle>
            <CardDescription className="text-cream/90">
              Visual representation of your cycle lengths over time
              {averageCycle && (
                <span className="ml-2 text-cream font-medium">
                  • Average: {averageCycle} days
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={prepareCycleChartData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0e6d6" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#7c2d12', fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tick={{ fill: '#7c2d12' }}
                    label={{ value: 'Days', angle: -90, position: 'insideLeft', fill: '#7c2d12' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #134e4a',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`${value} days`, 'Cycle Length']}
                  />
                  {/* Reference line for average */}
                  {averageCycle && (
                    <ReferenceLine 
                      y={averageCycle} 
                      stroke="#9f7e4d" 
                      strokeDasharray="5 5"
                      label={{ 
                        value: `Avg: ${averageCycle}`, 
                        fill: '#9f7e4d',
                        fontSize: 12,
                        position: 'right'
                      }}
                    />
                  )}
                  {/* Reference lines for normal range */}
                  <ReferenceLine 
                    y={21} 
                    stroke="#cbd5e1" 
                    strokeDasharray="3 3"
                  />
                  <ReferenceLine 
                    y={35} 
                    stroke="#cbd5e1" 
                    strokeDasharray="3 3"
                  />
                  <Bar dataKey="cycleLength" radius={[8, 8, 0, 0]}>
                    {prepareCycleChartData().map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.isNormal ? '#134e4a' : '#eab308'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-6 mt-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-dark-teal rounded"></div>
                <span className="text-burgundy/70">Normal (26-32 days)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-burgundy/70">Outside Normal Range</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-0.5 bg-gold"></div>
                <span className="text-burgundy/70">Your Average</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PeriodTracker;
